import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MarketingSource } from 'src/app/interfaces/MarketingSource/marketing-source';
import { MarketingSourceDetail } from 'src/app/interfaces/MarketingSource/marketing-source-detail';
import { GeneralService } from 'src/app/services/general.service';
import { MarketingSourceService } from 'src/app/services/marketing-source.service';
import { TranslatorService } from 'src/app/services/translator.service';

@Component({
  selector: 'app-marketing-source',
  templateUrl: './marketing-source.component.html',
  styleUrl: './marketing-source.component.scss',
  standalone: false
})
export class MarketingSourceComponent {

  @Input() public selectedItem: MarketingSource | any = null;
  @Input() public marketingSources: MarketingSource[] = [];
  @Input() public labelColCss: string = 'col-12 col-md-4';
  @Input() public inputColCss: string = 'col-12 col-md-8';

  @Input() value!: string;
  @Output() valueChange = new EventEmitter<string>();

  subSelectedItem: MarketingSourceDetail | any = null;
  isValid: boolean = false;
  currentLanguage: string = '';

  id: string = '';
  otherText = '';
  
  // Properties for filterable dropdown
  filterText: string = '';
  filteredDetails: MarketingSourceDetail[] = [];
  showDropdown: boolean = false;
  selectedIndex: number = -1;

  ngOnInit(): void {
    this.updateSelection();
    this.currentLanguage = this.translationService.getActiveLanguage().toUpperCase();
    this.initializeFilteredDetails();
  }
  constructor(public marketingSourceService: MarketingSourceService,
    private translationService: TranslatorService,
    private generalService: GeneralService) {

  }

  showDetailSelect(): boolean {
    let show = false;
    if (this.selectedItem?.details?.length > 0) {
      show = true;
    }
    return show;
  }

  ngAfterViewInit(): void {
    this.isValid = false;
  }

  onSelect(event: any): void {
    this.subSelectedItem = null;
    this.otherText = '';
    this.filterText = '';
    this.initializeFilteredDetails();
    this.updateSelection();
  }

  onSubSelect(event: any): void {
    this.otherText = '';
    this.updateSelection();
  }
  
  // Filter the details based on input text
  onFilterTextChange(): void {
    if (!this.selectedItem?.details) {
      this.filteredDetails = [];
      this.showDropdown = false;
      this.subSelectedItem = null;
      this.updateSelection();
      return;
    }
    
    const searchText = this.filterText.toLowerCase().trim();
    
    // Check if the current filter text exactly matches a selected item
    const exactMatch = this.selectedItem.details.find((detail: MarketingSourceDetail) => {
      return this.getDetailDisplay(detail).toLowerCase() === searchText;
    });
    
    if (exactMatch) {
      this.subSelectedItem = exactMatch;
      this.showDropdown = false;
    } else {
      // Clear selection if no exact match and filter the list
      if (this.subSelectedItem && this.getDetailDisplay(this.subSelectedItem).toLowerCase() !== searchText) {
        this.subSelectedItem = null;
      }
      
      if (searchText === '') {
        this.filteredDetails = [...this.selectedItem.details];
        this.showDropdown = this.filteredDetails.length > 0;
      } else {
        this.filteredDetails = this.selectedItem.details.filter((detail: MarketingSourceDetail) => {
          const displayText = this.getDetailDisplay(detail).toLowerCase();
          return displayText.includes(searchText);
        });
        this.showDropdown = this.filteredDetails.length > 0;
      }
      
      // Reset keyboard selection when filtering changes
      this.selectedIndex = -1;
    }
    
    this.updateSelection();
  }
  
  // Initialize filtered details when the main selection changes
  initializeFilteredDetails(): void {
    if (this.selectedItem?.details) {
      this.filteredDetails = [...this.selectedItem.details];
    } else {
      this.filteredDetails = [];
    }
    this.showDropdown = false;
  }
  
  // Select an item from the filtered dropdown
  selectFilteredItem(detail: MarketingSourceDetail): void {
    this.subSelectedItem = detail;
    this.filterText = this.getDetailDisplay(detail);
    this.showDropdown = false;
    this.updateSelection();
  }
  
  // Show dropdown when input is focused
  onFilterInputFocus(): void {
    if (this.filteredDetails.length > 0) {
      this.showDropdown = true;
      this.selectedIndex = -1;
    }
  }
  
  // Hide dropdown when clicking outside
  onFilterInputBlur(): void {
    // Delay hiding to allow click on dropdown items
    setTimeout(() => {
      this.showDropdown = false;
      this.selectedIndex = -1;
    }, 200);
  }
  
  // Handle keyboard navigation
  onFilterKeydown(event: KeyboardEvent): void {
    if (!this.showDropdown || this.filteredDetails.length === 0) {
      return;
    }
    
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, this.filteredDetails.length - 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
        break;
      case 'Enter':
        event.preventDefault();
        if (this.selectedIndex >= 0 && this.selectedIndex < this.filteredDetails.length) {
          this.selectFilteredItem(this.filteredDetails[this.selectedIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.showDropdown = false;
        this.selectedIndex = -1;
        break;
    }
  }

  textChanged(event: any): void {
    this.updateSelection();
  }

  private updateSelection() {
    let isValid = false;
    let selectedItemDisplay = '';
    if (this.selectedItem) {
      selectedItemDisplay = this.selectedItem.sourceEN;
      if (this.selectedItem.hasOptions) {
        if (this.subSelectedItem) {
          selectedItemDisplay = selectedItemDisplay + ': ' + this.subSelectedItem.detailEN;
          if (this.subSelectedItem.isCustomText) {
            selectedItemDisplay = selectedItemDisplay + ': ';
            if (this.otherText && this.otherText.length > 0) {
              selectedItemDisplay = selectedItemDisplay + this.otherText;
            }
            isValid = true;
          } else {
            isValid = true;
          }
        }
      } else {
        isValid = true;
      }
    }
    this.isValid = isValid;
    this.value = selectedItemDisplay;
    this.onValueChange();
  }

  onValueChange() {
    if (this.isValid) {
      this.valueChange.emit(this.value);
    } else {
      this.valueChange.emit('');
    }
  }

  translate(translationTag: string) {
    let translatedText: string = this.generalService.getTranslation(translationTag);

    if (translatedText === undefined) {
      translatedText = this.generalService.getTranslation(translationTag, 'base');
    }

    const lang = this.translationService.getActiveLanguage().toUpperCase();
    const componentScope = this;
    if (translationTag === 'Voodoo' && this.currentLanguage !== lang as string) {
      componentScope.currentLanguage = lang;
    }
  }

  getSourceDisplay(source: MarketingSource): string {
    let display = '';
    if (source) {
      switch (this.currentLanguage) {
        case 'ES': {
          display = source.sourceES!;
          break;
        }
        case 'FR': {
          display = source.sourceFR!;
          break;
        }
        default: {
          display = source.sourceEN!;
          break;
        }
      }
    }
    return display;
  }

  getDetailDisplay(detail: MarketingSourceDetail): string {
    let display = '';
    if (detail) {
      switch (this.currentLanguage) {
        case 'ES':
          display = detail.detailES!;
          break;
        case 'FR':
          display = detail.detailFR!;
          break;
        default:
          display = detail.detailEN!;
          break;
      }
    }
    return display;
  }
}
